import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { AlertCircle, History, Target } from 'lucide-react';
import { useFarmingResultsSelection } from '../../context/FarmingResultsSelectionContext';
import { 
  fetchLifetimeFarmingResults, 
  LifetimeFarmingResultsSummary, 
  LifetimeFarmingResultsData 
} from '../../services/lifetimeFarmingResultsService';

export const FarmingHistoryDashboard: React.FC = () => {
  const { selectedCompany, selectedProduct } = useFarmingResultsSelection();
  const [data, setData] = useState<LifetimeFarmingResultsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch data when company or product changes
  useEffect(() => {
    if (selectedCompany && selectedProduct) {
      fetchData();
    }
  }, [selectedCompany, selectedProduct]);

  const fetchData = async () => {
    if (!selectedCompany || !selectedProduct) return;

    const asin = selectedProduct.id || selectedProduct.ASIN;
    if (!asin) {
      setError('No ASIN found for selected product');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`Fetching Farming History data for: ${selectedCompany}/${asin}`);
      const result = await fetchLifetimeFarmingResults(selectedCompany, asin);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch Farming History data';
      console.error('Error fetching Farming History:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show selection prompt if no company or product selected
  if (!selectedCompany || !selectedProduct) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <Target className="h-12 w-12 text-app-primary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Select Company & ASIN
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Choose a company and ASIN from the sidebar to view Farming History data.
          </p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-app-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading Farming History...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Error Loading Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error}
          </p>
          <Button onClick={fetchData} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!data || data.farmingHistory.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No Farming History Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            No lifetime farming results data available for this ASIN.
          </p>
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number): string => {
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: number): string => {
    return value.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-app-primary" />
            Farming History
          </CardTitle>
          <CardDescription>
            Lifetime farming results for {selectedCompany} - {selectedProduct.id}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="w-full">
            <div className="w-full">
              {/* Header */}
              <div className="flex border-b dark:border-gray-700 py-4 px-4 font-semibold text-gray-900 dark:text-gray-100 text-sm">
                <div className="flex-1 text-center pr-6">Round of Farming</div>
                <div className="flex-1 text-center pr-6">Farming Period Start</div>
                <div className="flex-1 text-center pr-6">Farming Period End</div>
                <div className="flex-1 text-center pr-6">Days</div>
                <div className="flex-1 text-right pr-6">Spend</div>
                <div className="flex-1 text-right pr-6">Sales</div>
                <div className="flex-1 text-center pr-6">New CSTs</div>
                <div className="flex-1 text-right pr-6">Cost per CST</div>
                <div className="flex-1 text-right">Compared to Target $/CST</div>
              </div>
              
              {/* Body */}
              <div className="space-y-0">
                {data.farmingHistory.map((record, index) => (
                  <div key={`${record.asin}-${record.roundOfFarming}-${index}`} className="flex border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 py-4 px-4 text-sm">
                    <div className="flex-1 text-center text-gray-900 dark:text-gray-100 pr-6">
                      {record.roundOfFarming}
                    </div>
                    <div className="flex-1 text-center text-gray-900 dark:text-gray-100 pr-6">
                      {record.farmingPeriodStart}
                    </div>
                    <div className="flex-1 text-center text-gray-900 dark:text-gray-100 pr-6">
                      {record.farmingPeriodEnd}
                    </div>
                    <div className="flex-1 text-center text-gray-900 dark:text-gray-100 pr-6">
                      {formatNumber(record.days)}
                    </div>
                    <div className="flex-1 text-right text-gray-900 dark:text-gray-100 pr-6">
                      {formatCurrency(record.spend)}
                    </div>
                    <div className="flex-1 text-right text-gray-900 dark:text-gray-100 pr-6">
                      {formatCurrency(record.sales)}
                    </div>
                    <div className="flex-1 text-center text-gray-900 dark:text-gray-100 pr-6">
                      {formatNumber(record.newCsts)}
                    </div>
                    <div className="flex-1 text-right text-gray-900 dark:text-gray-100 pr-6">
                      {formatCurrency(record.costPerCst)}
                    </div>
                    <div className="flex-1 text-right">
                      {record.comparedToTargetCst !== undefined ? (
                        <span className={`font-medium ${
                          record.comparedToTargetCst > 0
                            ? 'text-red-600 dark:text-red-400'
                            : record.comparedToTargetCst < 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {record.comparedToTargetCst > 0 ? '+' : ''}{formatCurrency(record.comparedToTargetCst)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FarmingHistoryDashboard;